import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
  Paper, Stack, Grid, CircularProgress, Card
} from '@mui/material';
import {
  collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc
} from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { toast } from 'react-toastify';
import TablePagination from '@mui/material/TablePagination';

const Branches = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '' });
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [schoolId, setSchoolId] = useState(null);

  // Fetch schoolId from users collection
  const fetchSchoolId = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser?.uid));
      if (userDoc.exists()) {
        setSchoolId(userDoc.data().schoolId);
      } else {
        toast.error("User data not found");
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      toast.error("Error fetching user data");
    }
  };

  // Fetch branches for the school
  const fetchBranches = async (schoolId) => {
    if (!schoolId) return;
    try {
      const q = query(collection(db, 'branches'), where('schoolId', '==', schoolId));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBranches(data);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  useEffect(() => {
    fetchSchoolId();
  }, []);

  useEffect(() => {
    if (schoolId) fetchBranches(schoolId);
  }, [schoolId]);

  const handleOpen = () => {
    setFormData({ name: '' });
    setEditMode(false);
    setEditId(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
    setFormData({ name: '' });
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    const { name } = formData;
    const trimmedName = name.trim();

    if (!trimmedName) {
      toast.error("Branch name is required.");
      return;
    }

    if (!schoolId) {
      toast.error("School ID not found");
      return;
    }

    setLoading(true);

    try {
      const q = query(collection(db, 'branches'), where('schoolId', '==', schoolId));
      const snapshot = await getDocs(q);
      const existing = snapshot.docs.find(doc => doc.data().name.toLowerCase() === trimmedName.toLowerCase());

      if (!editMode && existing) {
        toast.error("Branch with this name already exists.");
        setLoading(false);
        return;
      }

      if (editMode && editId) {
        await updateDoc(doc(db, 'branches', editId), { name: trimmedName, schoolId });
        toast.success("Branch updated successfully.");
      } else {
        await addDoc(collection(db, 'branches'), { name: trimmedName, schoolId });
        toast.success("Branch added successfully.");
      }

      fetchBranches(schoolId);
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error("Error: " + error.message);
    }

    setLoading(false);
  };

  const handleEdit = (branch) => {
    setFormData({ name: branch.name });
    setEditId(branch.id);
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this branch?")) return;
    try {
      await deleteDoc(doc(db, 'branches', id));
      toast.success("Branch deleted");
      fetchBranches(schoolId);
    } catch (error) {
      console.error(error);
      toast.error("Error deleting branch");
    }
  };

  const paginated = branches.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button variant="contained" onClick={handleOpen}>Add Branch</Button>
        <Typography variant="h5" fontWeight="bold">Branches</Typography>
      </Stack>

      <Card>
        <Paper>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                <TableCell>#</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">No branches found</TableCell>
                </TableRow>
              ) : (
                paginated.map((branch, index) => (
                  <TableRow key={branch.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button variant="contained" size="small" onClick={() => handleEdit(branch)}>Edit</Button>
                        <Button variant="contained" color="error" size="small" onClick={() => handleDelete(branch.id)}>Delete</Button>
                      </Stack>
                    </TableCell>
                    <TableCell>{branch.name}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 20, 30]}
            component="div"
            count={branches.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Card>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editMode ? 'Edit Branch' : 'Add Branch'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Branch Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                size="small"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="error">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (editMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Branches;